import { Drawer } from 'antd';
import type { DrawerProps } from 'antd';
import classNames from 'clsx';
import { useIsDesktop } from 'hooks/useIsDesktop';
import React from 'react';

export type ResponsiveDrawerHeaderVariant = 'default' | 'blue' | 'white';

export interface ResponsiveDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  rootClassName?: DrawerProps['rootClassName'];
  width?: DrawerProps['width'];
  closable?: boolean;
  headerVariant?: ResponsiveDrawerHeaderVariant;
  mobileHeaderVariant?: ResponsiveDrawerHeaderVariant;
}

const MOBILE_PANEL_HEIGHT = 'calc(100dvh - 64px)';

const HEADER_VARIANT_CLASS_NAMES: Record<
  ResponsiveDrawerHeaderVariant,
  string
> = {
  default: '',
  blue: '[&_.ant-drawer-header]:!border-0 [&_.ant-drawer-header]:!bg-bleu-hellio-6 [&_.ant-drawer-header-title]:!text-white [&_.ant-drawer-close]:!text-white',
  white:
    '[&_.ant-drawer-header]:!bg-white [&_.ant-drawer-header_*]:text-black [&_.ant-drawer-title]:font-normal',
};

const ResponsiveDrawer: React.FC<ResponsiveDrawerProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  width = 560,
  className,
  rootClassName,
  closable = true,
  headerVariant = 'default',
  mobileHeaderVariant,
}) => {
  const isDesktop = useIsDesktop();
  const activeHeaderVariant =
    !isDesktop && mobileHeaderVariant ? mobileHeaderVariant : headerVariant;

  return (
    <Drawer
      title={title}
      placement={isDesktop ? 'right' : 'bottom'}
      open={open}
      onClose={onClose}
      width={isDesktop ? width : '100%'}
      height={isDesktop ? undefined : MOBILE_PANEL_HEIGHT}
      className={classNames(
        HEADER_VARIANT_CLASS_NAMES[activeHeaderVariant],
        '[&_.ant-drawer-body]:!p-0',
        !isDesktop &&
          '[&_.ant-drawer-content]:!overflow-hidden [&_.ant-drawer-content]:!rounded-t-lg [&_.ant-drawer-footer]:!px-3 [&_.ant-drawer-footer]:!py-3',
        className,
      )}
      rootClassName={classNames(
        !isDesktop && '[&_.ant-drawer-content-wrapper]:!bg-transparent',
        rootClassName,
      )}
      closable={closable}
      footer={footer}
      destroyOnClose
    >
      {children}
    </Drawer>
  );
};

/**
 * ResponsiveDrawer component with a white header and black title text.
 *
 * @param props - ResponsiveDrawerProps for configuring the drawer.
 * @returns A responsive drawer with white header styling.
 */
export function WhiteResponsiveDrawer(props: ResponsiveDrawerProps) {
  return <ResponsiveDrawer {...props} headerVariant="white" />;
}

export function BlueResponsiveDrawer(props: ResponsiveDrawerProps) {
  return <ResponsiveDrawer {...props} headerVariant="blue" />;
}

export default ResponsiveDrawer;
